<template>
	<div class="prop" :class="'type_' + spec.inputType">
		<div class="propWrapper">
			<div class="propLabel" v-if="spec.isReadonly"><span class="labelText" v-html="spec.labelHtml"></span><span class="readonlyValue">{{myValue}}</span></div>
			<template v-else>
				<label class="propLabel" :for="inputId" v-if="spec.inputType !== 'checkbox'"><span class="labelText" v-html="spec.labelHtml"></span><span class="rangeValue" v-if="spec.inputType === 'range'">{{myValue}}</span></label>
				<div v-if="spec.inputType === 'select'">
					<select class="propInput propSelect" :id="inputId" ref="inputEle" v-model="myValue" @change="onChange">
						<option v-for="v in spec.enumValues" :key="v">{{v}}</option>
					</select>
				</div>
				<div v-else-if="spec.inputType === 'array'" class="arrayEditor">
					<div class="arrayElement" v-for="(item, index) in arrayValues" :key="index">
						<select v-if="spec.allowedValues" class="propInput arrayInput" v-model="item.value" @change="onChange">
							<option v-for="v in spec.allowedValues" :key="v">{{v}}</option>
						</select>
						<input v-else class="propInput arrayInput" :type="spec.arrayType" v-model="item.value" @change="onChange" />
						<button type="button" class="arrayBtn arrayRemove" @click="removeArrayItem(index)" :title="'Remove item ' + (index + 1)">✕</button>
					</div>
					<div class="arrayEmpty" v-if="!arrayValues.length">(empty list)</div>
					<div class="arrayFooter">
						<button type="button" class="arrayBtn arrayAdd" @click="addArrayItem" title="Add a new item to the end of the list">+ Add item</button>
					</div>
				</div>
				<template v-else>
					<input class="propInput" :id="inputId" ref="inputEle" v-model="myValue" :type="spec.inputType" :min="spec.min" :max="spec.max" @change="onChange" @dblclick="onDoubleClick" />
					<label class="checkboxLabel" :for="inputId" v-html="spec.labelHtml" v-if="spec.inputType === 'checkbox'">
					</label>
				</template>
			</template>
		</div>
		<div class="help" v-if="spec.helpHtml" v-html="spec.helpHtml"></div>
	</div>
</template>

<script>

	export default {
		components: {},
		props:
		{
			initialValue: {
				required: true
			},
			spec: {
				type: Object, // An object containing the item key, default value, and other metadata.
				required: true
			}
		},
		data()
		{
			return {
				myUid: GetUid(),
				myValue: this.initialValue,
				arrayValues: null
			};
		},
		created()
		{
			if (this.spec.inputType === "array")
			{
				let arr = [];
				for (let i = 0; i < this.initialValue.length; i++)
					arr.push({ value: this.initialValue[i] }); // array values must be wrapped in an object to be compatible with v-model nested in v-for
				this.arrayValues = arr;
			}
		},
		mounted()
		{
		},
		computed:
		{
			inputId()
			{
				return "input_" + this.myUid;
			},
			myValueTypeEnforced()
			{
				if (this.spec.inputType === "number" || this.spec.inputType === "range")
					return new Number(this.myValue.toString()).valueOf();
				else if (this.spec.inputType === "array")
					return this.arrayValues.map(v => v.value);
				else
					return this.myValue;
			}
		},
		methods:
		{
			onChange(e)
			{
				this.$emit("valueChanged", this.spec.key, this.myValueTypeEnforced);
			},
			onDoubleClick(e)
			{
				this.myValue = this.spec.value;
				this.onChange(e);
			},
			addArrayItem()
			{
				if (this.spec.allowedValues && this.spec.allowedValues.length > 0)
					this.arrayValues.push({
						value: this.spec.allowedValues[0]
					});
				else
					this.arrayValues.push({
						value: null
					});
				this.onChange();
			},
			removeArrayItem(index)
			{
				this.arrayValues.splice(index, 1);
				this.onChange();
			},
			focus() // Called by parent component
			{
				if (this.$refs.inputEle)
					this.$refs.inputEle.focus();
			}
		},
		beforeUnmount()
		{
		}
	};
</script>

<style>
	/* Unscoped: the help text is rendered with v-html from Markdown, so scoped selectors would not reach it. */
	.prop .help > *:first-child
	{
		margin-top: 0px;
	}

	.prop .help > *:last-child
	{
		margin-bottom: 0px;
	}

	.prop .help p
	{
		margin: 0.5em 0px;
	}

	.prop .help ul,
	.prop .help ol
	{
		margin: 0.5em 0px;
		padding-left: 1.5em;
	}

	.prop .help li
	{
		margin: 0.15em 0px;
	}

	.prop .help strong
	{
		color: #1B3D52;
	}

	.prop .help a
	{
		color: var(--link-color);
	}

	.prop .help code
	{
		background-color: #E9EFF3;
		padding: 0px 4px;
		border: 1px solid #CBD8E0;
		border-radius: 3px;
		margin: 1px;
		font-family: Consolas, monospace;
		word-break: break-word;
	}

	.showHelp .help
	{
		display: block !important;
	}
</style>
<style scoped>
	.prop
	{
		display: flex;
		flex-direction: column;
		padding: 9px 10px;
		border-radius: 6px;
	}

	.propWrapper
	{
		flex: 1 1 auto;
		min-width: 0;
		max-width: var(--propFieldWidth, 400px);
	}

	.propLabel
	{
		display: block;
		margin-bottom: 3px;
		font-weight: bold;
		color: #24303A;
		/* overflow-wrap (not word-break) so a long field name does not reduce the label's min-content width, which
		   would let the shrink-to-fit modal dialogs collapse and break these identifiers mid-word. */
		overflow-wrap: break-word;
	}

	.rangeValue,
	.readonlyValue
	{
		display: inline-block;
		margin-left: 8px;
		padding: 0px 6px;
		border: 1px solid #CBD8E0;
		border-radius: 3px;
		background-color: #F1F5F8;
		font-weight: normal;
		font-family: Consolas, monospace;
		word-break: break-all;
	}

	.checkboxLabel
	{
		user-select: none;
		font-weight: bold;
		color: #24303A;
		cursor: pointer;
		overflow-wrap: break-word;
	}

	/* Checkbox fields put the box and its label on a single row. */
	.type_checkbox .propWrapper
	{
		display: flex;
		align-items: center;
		gap: 8px;
	}

	input[type="checkbox"]
	{
		flex: 0 0 auto;
		width: 16px;
		height: 16px;
		margin: 0px;
		accent-color: var(--primary-color);
		cursor: pointer;
	}

	input[type="text"],
	input[type="number"],
	input[type="range"],
	input[type="color"],
	.propSelect
	{
		display: block;
		width: 100%;
		box-sizing: border-box;
	}

	input[type="text"],
	input[type="number"],
	input[type="password"],
	.propSelect,
	.arrayInput
	{
		padding: 5px 7px;
		border: 1px solid #BFCBD4;
		border-radius: 4px;
		background-color: #FFFFFF;
		color: #000000;
		font-family: inherit;
		font-size: 14px;
	}

		input[type="text"]:hover,
		input[type="number"]:hover,
		.propSelect:hover,
		.arrayInput:hover
		{
			border-color: #93A6B4;
		}

		input[type="text"]:focus,
		input[type="number"]:focus,
		.propSelect:focus,
		.arrayInput:focus
		{
			outline: none;
			border-color: var(--primary-color);
			box-shadow: 0px 0px 0px 3px rgba(26,105,153,0.18);
		}

	/* A dropdown as wide as a text field looks silly for short enum names, but it should not be cramped either. */
	.propSelect
	{
		width: auto;
		min-width: 240px;
		max-width: 100%;
	}

	/* ---- List (array) editor ---------------------------------------------------------------------------------- */
	.arrayEditor
	{
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.arrayElement
	{
		display: flex;
		align-items: center;
		gap: 6px;
	}

	/* The list items hold long values (ARNs, UNC paths), so they get the full width of the field column. */
	.arrayElement input.arrayInput,
	.arrayElement select.arrayInput
	{
		display: block;
		flex: 1 1 auto;
		width: auto;
		min-width: 0;
		box-sizing: border-box;
	}

		/* Numeric list items are a few characters at most; the full field width would just look broken. */
		.arrayElement input.arrayInput[type="number"]
		{
			flex: 0 1 220px;
		}

	.arrayEmpty
	{
		color: #7A8894;
		font-style: italic;
	}

	.arrayFooter
	{
		display: flex;
		align-items: center;
		gap: 10px;
		margin-top: 2px;
	}

	.arrayBtn
	{
		flex: 0 0 auto;
		border: 1px solid #BFCBD4;
		border-radius: 4px;
		background-color: #F6F8FA;
		color: #24303A;
		font-family: inherit;
		font-size: 13px;
		cursor: pointer;
		padding: 4px 9px;
	}

		.arrayBtn:hover
		{
			background-color: #FFFFFF;
			border-color: var(--primary-color);
		}

	.arrayRemove
	{
		width: 28px;
		padding: 4px 0px;
		line-height: 1;
		color: #8A3030;
	}

		.arrayRemove:hover
		{
			background-color: #FFECEC;
			border-color: #C05050;
		}

	/* ---- Help text ------------------------------------------------------------------------------------------- */
	.help
	{
		display: none;
		flex: 1 1 auto;
		min-width: 0;
		padding: 8px 12px;
		border-left: 3px solid var(--primary-color-light2);
		border-radius: 4px;
		color: #243642;
		background-color: #F3F8FC;
		margin: 6px 0px 0px 0px;
		font-size: 0.92em;
		line-height: 1.45;
	}

	@media (min-width: 600px)
	{
		.prop
		{
			flex-direction: row;
			align-items: center;
			gap: 16px;
		}

		/* A firm flex-basis (rather than grow + max-width) so a field with a lot of help text beside it is not
		   squeezed down to its min-content width, which would wrap the label and shrink the input to a few
		   characters.  It still shrinks when the row itself is narrow, e.g. inside a modal dialog. */
		.propWrapper
		{
			flex: 0 1 var(--propFieldWidth, 400px);
			max-width: 100%;
		}

		.help
		{
			flex: 1 1 40%;
			min-width: 200px;
			margin: 0px;
		}
	}
</style>
